import Section from "@/components/settings/Section";
import LanguagesSelector, { LanguageWithProficiency } from "@/components/shared/LanguagesSelector";
import { useAPI } from "@/contexts/APIContext";
import { useSettings } from "@/contexts/SettingsContext";
import handleError from "@/utils/handleError";
import { useEffect, useState } from "react";

interface LanguagesSectionProps {
  profile: any;
}

export default function LanguagesSection({ profile }: LanguagesSectionProps) {
  const { api, updateUser } = useAPI();
  const { setSuccessMessage, setErrorMessage } = useSettings();

  const [languagesToLearn, setLanguagesToLearn] = useState<LanguageWithProficiency[]>([]);
  const [originalLanguagesToLearn, setOriginalLanguagesToLearn] = useState<LanguageWithProficiency[]>([]);
  const [knownLanguages, setKnownLanguages] = useState<LanguageWithProficiency[]>([]);
  const [originalKnownLanguages, setOriginalKnownLanguages] = useState<LanguageWithProficiency[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  // Initialize languages data
  useEffect(() => {
    if (!profile || !profile.languages) return;

    // Separate languages into learning and known
    const learning = profile.languages
      .filter((lang: any) => lang.learning)
      .map((lang: any) => ({
        languageCode: lang.languageCode,
        proficiency: lang.proficiency,
      }));

    const known = profile.languages
      .filter((lang: any) => !lang.learning)
      .map((lang: any) => ({
        languageCode: lang.languageCode,
        proficiency: lang.proficiency,
      }));

    setLanguagesToLearn(learning);
    setOriginalLanguagesToLearn([...learning]);

    setKnownLanguages(known);
    setOriginalKnownLanguages([...known]);
  }, [profile]);

  // Check for changes
  useEffect(() => {
    if (!originalLanguagesToLearn || !originalKnownLanguages) return;

    // Check if languages to learn have changed
    const learnChanged =
      languagesToLearn.length !== originalLanguagesToLearn.length ||
      languagesToLearn.some((lang, i) => {
        if (i >= originalLanguagesToLearn.length) return true;
        return (
          lang.languageCode !== originalLanguagesToLearn[i].languageCode ||
          Math.abs(lang.proficiency - originalLanguagesToLearn[i].proficiency) > 0.01
        );
      });

    // Check if known languages have changed
    const knownChanged =
      knownLanguages.length !== originalKnownLanguages.length ||
      knownLanguages.some((lang, i) => {
        if (i >= originalKnownLanguages.length) return true;
        return lang.languageCode !== originalKnownLanguages[i].languageCode;
      });

    const changed = learnChanged || knownChanged;
    setIsChanged(changed);
  }, [languagesToLearn, knownLanguages, originalLanguagesToLearn, originalKnownLanguages]);

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // First, clear existing languages
      if (profile?.languages) {
        for (const lang of profile.languages) {
          try {
            await api.user.language.delete({ languageCode: lang.languageCode });
          } catch (error) {
            // Ignore errors when deleting non-existent languages
            console.warn(`Failed to delete language ${lang.languageCode}`, error);
          }
        }
      }

      // Add languages to learn
      for (const lang of languagesToLearn) {
        await api.user.language.create({
          languageCode: lang.languageCode,
          proficiency: lang.proficiency,
          target: true,
        });
      }

      // Add known languages
      for (const lang of knownLanguages) {
        await api.user.language.create({
          languageCode: lang.languageCode,
          proficiency: lang.proficiency,
          target: false,
        } as any);
      }

      setOriginalLanguagesToLearn([...languagesToLearn]);
      setOriginalKnownLanguages([...knownLanguages]);
      await updateUser(true);
      setSuccessMessage("Languages updated successfully");
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to update languages");
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Section title="Languages" onSave={handleSave} isChanged={isChanged} isSaving={isSaving} id="languages">
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-700 mb-3">Languages You Speak</h3>
        <LanguagesSelector languages={knownLanguages} onChange={setKnownLanguages} withProficiency={false} />
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-700 mb-3">Languages You Want to Learn</h3>
        <LanguagesSelector languages={languagesToLearn} onChange={setLanguagesToLearn} withProficiency={true} />
      </div>
    </Section>
  );
}
